import React, { memo, useMemo, useCallback } from "react";
// components
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { SpriteIconWeb } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb";
// styles
import {
    HalfCardWrapper,
    ImageContainer,
    TextHeading,
    ItemListWrapper,
    ItemWrapper,
    ItemImageWrapper,
    ItemTextWrapper,
    ButtonWrapper,
    PrimaryTextButton,
    SecondaryTextButton,
    DisclaimerWrapper,
    DisclaimerText,
    HorizontalCarouselContainer,
    HorizontalCarouselWrapper,
    PaginationDots,
    PaginationDot,
} from "./styles";
// constants
import {
    IM_GIFT_BAG_HALF_CARD_IMAGE_URL,
    TEXTS,
    IM_GIFTBAG_HALFCARD_DATA_TEST_IDS as IDS,
    CAROUSEL_CONFIG,
} from "./constants";
// utils
import { isEmpty, useEffectOnce } from "@swiggy-private/core-ui";
// hooks
import { useCarousel } from "./useCarousel";
// types
import { IInstamartGiftingWidgetPopUpDetails } from "@swiggy-private/cart-types";

export interface GiftingIMGiftBagHalfcardProps {
    isOpen: boolean;
    onCloseHandler: () => void;
    items: {
        imageId: string;
        title: string;
        subtitle: string;
    }[];
    onPrimaryClickHandler: () => void;
    onSecondaryClickHandler: () => void;
    popupDetails?: IInstamartGiftingWidgetPopUpDetails;
    impressionHalfCard?: () => void;
    impressionPrimaryButton?: () => void;
    impressionSecondaryButton?: () => void;
    clickCrossCTA?: () => void;
    clickPrimaryButton?: () => void;
    clickSecondaryButton?: () => void;
}

const GiftingIMGiftBagHalfcard = ({
    isOpen,
    onCloseHandler,
    items,
    onPrimaryClickHandler,
    onSecondaryClickHandler,
    popupDetails,
    impressionHalfCard,
    impressionPrimaryButton,
    impressionSecondaryButton,
    clickCrossCTA,
    clickPrimaryButton,
    clickSecondaryButton,
}: GiftingIMGiftBagHalfcardProps) => {
    const isEmptyItems = isEmpty(items);
    const payload = {
        cardWidth: CAROUSEL_CONFIG.CARD_WIDTH,
        gap: CAROUSEL_CONFIG.GAP,
        totalPages: items?.length,
        interval: CAROUSEL_CONFIG.AUTO_SCROLL_INTERVAL,
    };
    const { containerRef, currentPage } = useCarousel(payload);

    const isSingleItem = useMemo(() => {
        return items?.length === 1;
    }, [items]);

    const { primaryCTAText, secondaryCTAText } = useMemo(() => {
        const primaryCTA = popupDetails?.ctaButtons?.find((cta) => cta?.giftBagPackagingEnabled === true);
        const secondaryCTA = popupDetails?.ctaButtons?.find((cta) => cta?.giftBagPackagingEnabled === false);
        return {
            primaryCTAText: primaryCTA?.text || TEXTS.PRIMARY_BUTTON_TEXT,
            secondaryCTAText: secondaryCTA?.text || TEXTS.SECONDARY_BUTTON_TEXT,
        };
    }, [popupDetails]);

    const onCloseClickHandler = useCallback(() => {
        clickCrossCTA?.();
        onCloseHandler();
    }, [clickCrossCTA, onCloseHandler]);

    const onPrimaryClick = useCallback(() => {
        clickPrimaryButton?.();
        onPrimaryClickHandler();
    }, [clickPrimaryButton, onPrimaryClickHandler]);

    const onSecondaryClick = useCallback(() => {
        clickSecondaryButton?.();
        onSecondaryClickHandler();
    }, [clickSecondaryButton, onSecondaryClickHandler]);

    useEffectOnce(() => {
        if (!isEmpty(popupDetails)) {
            impressionHalfCard?.();
            impressionPrimaryButton?.();
            impressionSecondaryButton?.();
        }
    });

    if (isEmptyItems) {
        return null;
    }

    return (
        <HalfCard isOpen={isOpen} hasCustomPadding closeOnOverlayClick onClose={onCloseClickHandler}>
            <HalfCardWrapper data-testid={IDS.CONTAINER} bgImage={popupDetails?.backgroundImageCreativeId}>
                <ImageContainer data-testid={IDS.IMAGE_CONTAINER}>
                    <Image
                        cloudinaryId={popupDetails?.creativeId || IM_GIFT_BAG_HALF_CARD_IMAGE_URL}
                        width="100%"
                        height="100%"
                        fit="cover"
                    />
                </ImageContainer>
                <TextHeading variant="V2.heading1.neutral.black" color="text_High_Emphasis" data-testid={IDS.HEADING}>
                    {popupDetails?.description || TEXTS.HEADING}
                </TextHeading>
                {!isEmpty(items) && (
                    <ItemListWrapper data-testid={IDS.ITEM_LIST}>
                        <HorizontalCarouselContainer ref={containerRef} isSingleItem={isSingleItem}>
                            <HorizontalCarouselWrapper>
                                {items.map((item, index) => (
                                    <ItemWrapper isSingleItem={isSingleItem} key={index}>
                                        <ItemImageWrapper>
                                            <Image cloudinaryId={item.imageId} width="100%" height="100%" fit="cover" />
                                        </ItemImageWrapper>
                                        <ItemTextWrapper>
                                            {!isEmpty(item.title) && (
                                                <Text
                                                    variant="V2.overline.neutral.medium"
                                                    color="text_High_Emphasis"
                                                    maxLines={2}
                                                >
                                                    {item.title}
                                                </Text>
                                            )}
                                        </ItemTextWrapper>
                                    </ItemWrapper>
                                ))}
                            </HorizontalCarouselWrapper>
                        </HorizontalCarouselContainer>
                        <PaginationDots>
                            {!isSingleItem &&
                                items.map((_, index) => <PaginationDot key={index} isActive={index === currentPage} />)}
                        </PaginationDots>
                    </ItemListWrapper>
                )}
                {!isEmpty(popupDetails?.disclaimer) && (
                    <DisclaimerWrapper data-testid={IDS.DISCLAIMER}>
                        <SpriteIconWeb
                            iconName="info16"
                            strokeColor="text_Low_Emphasis"
                            fillColor="text_Low_Emphasis"
                        />
                        <DisclaimerText variant="V2.body3.neutral.medium" color="text_Low_Emphasis">
                            {popupDetails?.disclaimer}
                        </DisclaimerText>
                    </DisclaimerWrapper>
                )}
                <ButtonWrapper>
                    <PrimaryTextButton
                        onClick={onPrimaryClick}
                        variant="primary"
                        type="button"
                        data-testid={IDS.PRIMARY_BUTTON}
                    >
                        {primaryCTAText}
                    </PrimaryTextButton>
                    <SecondaryTextButton
                        onClick={onSecondaryClick}
                        color="primary"
                        type="button"
                        buttonSize="medium"
                        data-testid={IDS.SECONDARY_BUTTON}
                    >
                        {secondaryCTAText}
                    </SecondaryTextButton>
                </ButtonWrapper>
            </HalfCardWrapper>
        </HalfCard>
    );
};

export default memo(GiftingIMGiftBagHalfcard);
